import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeRotationState } from '../theme/useThemeRotation';

// ============================================
// TYPES
// ============================================
interface ThemeControllerProps {
  state: ThemeRotationState;
}

// ============================================
// THEME CONTROLLER COMPONENT
// ============================================
const ThemeController: React.FC<ThemeControllerProps> = ({ state }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    styleIndex,
    presetIndex,
    autoplay,
    duration,
    isPaused,
    currentStyle,
    currentPreset,
    styles,
    timeRemaining,
    setStyle,
    setPreset,
    toggleAutoplay,
    setDuration,
    next,
    prev,
    pause,
    resume,
  } = state;

  // Pause when expanded or hovered, resume when closed and not hovered
  useEffect(() => {
    if (isExpanded || isHovered) {
      pause();
    } else {
      resume();
    }
  }, [isExpanded, isHovered, pause, resume]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 10);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isExpanded]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded]);

  // Format time remaining
  const formatTime = (seconds: number) => `${seconds}s`;

  return (
    <div
      ref={containerRef}
      className="fixed bottom-4 left-4 z-[100]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Compact toggle button - responsive sizing */}
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <span className="material-symbols-outlined text-white text-base sm:text-lg">
          {isExpanded ? 'close' : 'palette'}
        </span>
        <span className="text-white text-[10px] sm:text-xs font-medium uppercase tracking-wider hidden xs:inline sm:inline">
          Theme
        </span>
        {autoplay && !isExpanded && (
          <span className="text-white/50 text-[9px] sm:text-[10px] font-mono">
            {formatTime(timeRemaining)}
          </span>
        )}
      </motion.button>

      {/* Expanded panel - responsive positioning */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute bottom-12 left-0 w-[calc(100vw-2rem)] sm:w-80 max-w-80 rounded-xl overflow-hidden"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            }}
          >
            {/* Header */}
            <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-white text-xs sm:text-sm font-semibold">Theme Controller</span>
                <div className="flex items-center gap-2">
                  {autoplay && (
                    <span className="text-white/50 text-[9px] sm:text-[10px] font-mono bg-white/5 px-1.5 sm:px-2 py-0.5 rounded">
                      Paused
                    </span>
                  )}
                </div>
              </div>
              <p className="text-white/40 text-[9px] sm:text-[10px] mt-1">
                {currentStyle.name} — {currentPreset.name}
              </p>
            </div>

            {/* Content */}
            <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Style selector */}
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-white/60 text-[9px] sm:text-[10px] uppercase tracking-wider font-medium">
                  Style
                </label>
                <div className="grid grid-cols-5 gap-1">
                  {styles.map((style, index) => (
                    <button
                      key={style.id}
                      onClick={() => setStyle(index)}
                      className={`px-1.5 sm:px-2 py-1 sm:py-1.5 text-[8px] sm:text-[9px] font-medium uppercase tracking-wider rounded transition-all ${
                        styleIndex === index
                          ? 'bg-white text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                      title={style.name}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                <div className="text-white/40 text-[9px] sm:text-[10px]">
                  {currentStyle.name}
                </div>
              </div>

              {/* Preset selector */}
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-white/60 text-[9px] sm:text-[10px] uppercase tracking-wider font-medium">
                  Preset ({currentStyle.presets.length})
                </label>
                <div className="flex flex-wrap gap-1">
                  {currentStyle.presets.map((preset, index) => (
                    <button
                      key={preset.id}
                      onClick={() => setPreset(index)}
                      className={`px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[10px] font-medium rounded transition-all ${
                        presetIndex === index
                          ? 'bg-white text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Navigation buttons */}
              <div className="flex gap-2">
                <button
                  onClick={prev}
                  className="flex-1 flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-white/5 text-white/60 rounded hover:bg-white/10 hover:text-white transition-all"
                >
                  <span className="material-symbols-outlined text-xs sm:text-sm">chevron_left</span>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-wider">Prev</span>
                </button>
                <button
                  onClick={next}
                  className="flex-1 flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-white/5 text-white/60 rounded hover:bg-white/10 hover:text-white transition-all"
                >
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-wider">Next</span>
                  <span className="material-symbols-outlined text-xs sm:text-sm">chevron_right</span>
                </button>
              </div>

              {/* Divider */}
              <div className="border-t border-white/10" />

              {/* Autoplay toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-white text-[10px] sm:text-xs font-medium">Autoplay</label>
                  <p className="text-white/40 text-[9px] sm:text-[10px]">
                    {autoplay ? `Every ${duration}s` : 'Off'}
                  </p>
                </div>
                <button
                  onClick={toggleAutoplay}
                  className={`relative w-10 sm:w-12 h-5 sm:h-6 rounded-full transition-colors ${
                    autoplay ? 'bg-white' : 'bg-white/20'
                  }`}
                  role="switch"
                  aria-checked={autoplay}
                >
                  <motion.div
                    className={`absolute top-0.5 sm:top-1 w-3.5 sm:w-4 h-3.5 sm:h-4 rounded-full ${
                      autoplay ? 'bg-black' : 'bg-white/60'
                    }`}
                    animate={{ left: autoplay ? '22px' : '2px' }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                </button>
              </div>

              {/* Duration control */}
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-white/60 text-[9px] sm:text-[10px] uppercase tracking-wider font-medium">
                    Duration
                  </label>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <input
                      type="number"
                      min={5}
                      max={60}
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value) || 10)}
                      className="w-12 sm:w-14 px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs text-white bg-white/5 border border-white/10 rounded text-center focus:outline-none focus:border-white/30"
                    />
                    <span className="text-white/40 text-[9px] sm:text-[10px]">sec</span>
                  </div>
                </div>
                <input
                  type="range"
                  min={5}
                  max={60}
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white"
                  style={{
                    background: `linear-gradient(to right, white ${((duration - 5) / 55) * 100}%, rgba(255,255,255,0.1) ${((duration - 5) / 55) * 100}%)`,
                  }}
                />
              </div>

              {/* Status */}
              <div className="pt-2 border-t border-white/10">
                <div className="flex items-center gap-2 text-white/30 text-[9px] sm:text-[10px]">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    isPaused ? 'bg-yellow-500' : autoplay ? 'bg-green-500' : 'bg-white/30'
                  }`} />
                  <span>
                    {isPaused ? 'Paused' : autoplay ? 'Auto' : 'Manual'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThemeController;
